/**
 * MyProfileView — a candidate edits their own public-facing profile.
 *
 * The gap this closes is not hypothetical. Both live candidates' bios and
 * Facebook links were typed into production by hand, in SQL, over ssh, because
 * the product had no in-app way for a candidate to write them — and neither
 * had a headshot at all, because there was no upload path for anyone to use.
 * This view is the end of that per-customer DBA work.
 *
 * What a candidate may change here, and what they may not, is a permissions
 * boundary rather than a layout choice:
 *
 *   editable   display name, bio, website, donate link, socials, headshot
 *   read-only  office, party, district, election date, active state, slate
 *              position, geographic reach, ballot name, slug
 *
 * The read-only half is RENDERED, not hidden. A candidate who cannot see their
 * own office has no way to notice it is wrong, and "ask your committee" is
 * only actionable if they know what to ask about. Hiding it would also imply
 * the omission is the boundary, when the boundary is the server: PATCH
 * /candidates/{id}/profile accepts exactly five keys and 422s anything else,
 * so this form could not widen its own reach even if it tried to.
 *
 * Pattern follows MyVolunteersView (the other candidate-only self-service
 * surface): axios + useState/useEffect, ViewHeader, semantic Tailwind tokens,
 * useIsMobile for structural swaps, strictly typed.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  UserSquare2,
  ShieldAlert,
  Upload,
  Trash2,
  Plus,
  X,
  Save,
  ImageOff,
} from 'lucide-react';
import { ViewHeader } from '@/components/ViewHeader';
import { InfoTip } from '@/components/InfoTip';
import { Skeleton } from '@/components/Skeleton';
import { useIsMobile } from '@/hooks/useMediaQuery';
import {
  getCandidate,
  updateMyCandidateProfile,
  uploadCandidateHeadshot,
  deleteCandidateHeadshot,
  headshotUrl,
} from '@/api/candidates';
import { listMyCandidates, type MyCandidate } from '@/api/delegation';
import {
  CANDIDATE_SOCIAL_PLATFORMS,
  CANDIDATE_SOCIAL_PLATFORM_LABELS,
  type CandidateRead,
  type CandidateSocialInput,
  type CandidateSocialPlatform,
} from '@/types/candidate';

// ---------------------------------------------------------------------------
// Upload limits
//
// Mirrored from `HEADSHOT_MAX_SIZE_BYTES` / `PHOTO_ALLOWED_TYPES` in
// backend/app/config.py. This is a MIRROR and the server is the authority —
// the point of checking here is that rejecting a 40 MB camera original before
// it crosses a rural LTE link is kinder than rejecting it after. A drift
// between the two shows up as a 422 the user still sees, never as an upload
// the client wrongly allowed.
// ---------------------------------------------------------------------------

const MAX_HEADSHOT_BYTES = 5 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

function errorDetail(err: unknown): string {
  const maybe = err as { response?: { data?: { detail?: unknown }; status?: number } };
  const detail = maybe?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  // A 422 from `extra="forbid"` arrives as an array of field errors rather
  // than a string. Surface the first one instead of "Request failed (422)",
  // which would hide exactly the field the server refused.
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0] as { msg?: string; loc?: unknown[] };
    const field = Array.isArray(first?.loc) ? first.loc[first.loc.length - 1] : null;
    if (first?.msg) return field ? `${String(field)}: ${first.msg}` : first.msg;
  }
  const status = maybe?.response?.status;
  if (status) return `Request failed (${status}). Please try again.`;
  return 'Could not reach the backend. Please try again.';
}

/** Trimmed, with empty meaning "cleared" rather than "empty string". */
function nullIfBlank(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

// ---------------------------------------------------------------------------
// View
// ---------------------------------------------------------------------------

export interface MyProfileViewProps {
  userRole?: string;
}

export function MyProfileView({ userRole }: MyProfileViewProps) {
  const roleNorm = typeof userRole === 'string' ? userRole.toUpperCase() : '';
  if (roleNorm !== 'CANDIDATE') {
    return (
      <div className="space-y-6">
        <ViewHeader
          title={
            <span className="flex items-center gap-2">
              <UserSquare2 className="w-6 h-6 text-accent" />
              My Profile
            </span>
          }
          tagline="Your photo, bio, and links"
        />
        <div className="max-w-2xl rounded-lg border border-status-danger-bg/30 bg-status-danger-bg/20 p-6">
          <h2 className="text-lg font-semibold text-status-danger mb-2 flex items-center gap-2">
            <ShieldAlert className="w-5 h-5" aria-hidden="true" />
            Access restricted
          </h2>
          <p className="text-fg-secondary">
            My Profile edits the record of the candidate you are signed in as. If
            you manage candidates for your committee, use the Candidates admin
            view instead — it can edit every candidate in your organization.
          </p>
        </div>
      </div>
    );
  }

  return <MyProfileInner />;
}

function MyProfileInner() {
  const isMobile = useIsMobile();

  const [link, setLink] = useState<MyCandidate | null>(null);
  const [record, setRecord] = useState<CandidateRead | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    (async () => {
      try {
        const rows = await listMyCandidates();
        // Only a `relation: 'candidate'` link is "you". A staff link means the
        // account works FOR a candidate, which the server refuses here with a
        // 403 — so don't offer a form that is going to be rejected.
        const own = rows.find((r) => r.relation === 'candidate') ?? null;
        if (cancelled) return;
        setLink(own);
        if (!own) {
          setRecord(null);
          setLoading(false);
          return;
        }
        // GET /candidates/{id} rather than reusing the /mine row: /mine is the
        // delegation shape (reach + can_delegate) and carries no bio, socials,
        // or headshot. Hydrating a form from the lighter shape is exactly the
        // bug `types/candidate.ts` warns about — it would blank every field it
        // does not know about on the next save.
        const full = await getCandidate(own.candidate_id);
        if (cancelled) return;
        setRecord(full);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setLoadError(errorDetail(err));
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [reload]);

  return (
    <div className="space-y-6">
      <ViewHeader
        title={
          <span className="flex items-center gap-2">
            <UserSquare2 className="w-6 h-6 text-accent" />
            My Profile
          </span>
        }
        tagline="Your photo, bio, and links — the things voters see"
        inline={
          <InfoTip
            term={{
              title: 'What you can edit here',
              short:
                'Your photo, display name, bio, website, donate link, and social accounts.',
              long:
                'Your office, party, district, election date and turf are set by your committee and are read-only here — they decide which doors and which responses belong to your race, so changing them would change what past canvassing means. Ask your committee admin if any of them is wrong.',
            }}
          />
        }
      />

      {loadError && (
        <div className="bg-status-danger-bg/30 rounded-xl p-4 border border-status-danger-bg text-status-danger text-sm flex items-center justify-between gap-3">
          <span>{loadError}</span>
          <button
            type="button"
            onClick={() => setReload((n) => n + 1)}
            className="bg-accent hover:bg-accent-hover text-white px-3 py-1.5 rounded text-xs font-bold"
          >
            Retry
          </button>
        </div>
      )}

      {loading && !record && (
        <div className="space-y-3">
          <Skeleton className="h-40" />
          <Skeleton className="h-64" />
        </div>
      )}

      {!loading && !loadError && !link && (
        <div className="max-w-2xl rounded-xl border border-border-base bg-bg-raised p-6">
          <h2 className="text-lg font-semibold text-fg-primary mb-2">
            No candidate record linked to this account
          </h2>
          <p className="text-fg-secondary">
            Your sign-in isn&apos;t connected to a candidate yet, so there is no
            profile to edit. Ask your committee admin to link it.
          </p>
        </div>
      )}

      {!loading && !loadError && record && (
        <>
          <HeadshotCard
            record={record}
            isMobile={isMobile}
            onChanged={(updated) => setRecord(updated)}
          />
          <ProfileForm
            key={record.id}
            record={record}
            isMobile={isMobile}
            onSaved={(updated) => setRecord(updated)}
          />
          <BallotFactsCard record={record} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Headshot
// ---------------------------------------------------------------------------

function HeadshotCard({
  record,
  isMobile,
  onChanged,
}: {
  record: CandidateRead;
  isMobile: boolean;
  onChanged: (updated: CandidateRead) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [pending, setPending] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Object URLs are a manual-lifetime resource: one revoke per create, and the
  // revoke has to happen when the URL is REPLACED, not only on unmount, or a
  // candidate trying three photos leaks two blobs for the session.
  useEffect(() => {
    if (!pending) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(pending);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pending]);

  const clearPending = useCallback(() => {
    setPending(null);
    if (inputRef.current) inputRef.current.value = '';
  }, []);

  const handlePick = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setNotice(null);
    const file = e.target.files?.[0] ?? null;
    if (!file) {
      setPending(null);
      return;
    }
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setError('Choose a JPEG, PNG, or WebP image.');
      setPending(null);
      e.target.value = '';
      return;
    }
    if (file.size > MAX_HEADSHOT_BYTES) {
      setError(
        `That image is ${(file.size / (1024 * 1024)).toFixed(1)} MB. The limit is 5 MB — try exporting it smaller.`,
      );
      setPending(null);
      e.target.value = '';
      return;
    }
    setPending(file);
  }, []);

  const handleUpload = useCallback(async () => {
    if (!pending) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const updated = await uploadCandidateHeadshot(record.id, pending);
      onChanged(updated);
      clearPending();
      setNotice('Photo updated.');
    } catch (err) {
      setError(errorDetail(err));
    } finally {
      setBusy(false);
    }
  }, [pending, record.id, onChanged, clearPending]);

  const handleRemove = useCallback(async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const updated = await deleteCandidateHeadshot(record.id);
      onChanged(updated);
      clearPending();
      setNotice('Photo removed.');
    } catch (err) {
      setError(errorDetail(err));
    } finally {
      setBusy(false);
    }
  }, [record.id, onChanged, clearPending]);

  // The preview wins over the stored image while a file is staged: the
  // candidate should see what they are about to commit, not what is still
  // there. `pending` is also what makes Upload meaningful — without a staged
  // file the button has nothing to send.
  const shown = previewUrl ?? (record.headshot_path ? headshotUrl(record.headshot_path) : null);

  return (
    <div className="rounded-xl border border-border-base bg-bg-raised p-5">
      <div className="text-[10px] uppercase tracking-wider text-accent font-semibold mb-3">
        Your photo
      </div>

      <div className={isMobile ? 'flex flex-col gap-4' : 'flex items-start gap-6'}>
        <div className="shrink-0">
          {shown ? (
            <img
              src={shown}
              alt={`Headshot of ${record.full_name}`}
              className="w-32 h-32 rounded-lg object-cover border border-border-base bg-bg-base"
            />
          ) : (
            <div className="w-32 h-32 rounded-lg border border-dashed border-border-base bg-bg-base flex flex-col items-center justify-center gap-1 text-fg-muted">
              <ImageOff className="w-6 h-6" aria-hidden="true" />
              <span className="text-[10px]">No photo</span>
            </div>
          )}
          {previewUrl && (
            <div className="text-[10px] text-status-warning mt-1.5 text-center">
              Not saved yet
            </div>
          )}
        </div>

        <div className="flex-1 space-y-3">
          <p className="text-sm text-fg-secondary">
            A head-and-shoulders photo, JPEG / PNG / WebP, up to 5 MB. Location
            data is stripped from the file before it is stored.
          </p>

          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_IMAGE_TYPES.join(',')}
            onChange={handlePick}
            className="block w-full text-sm text-fg-secondary file:mr-3 file:rounded file:border-0 file:bg-bg-base file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-fg-primary hover:file:bg-border-base"
          />

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleUpload}
              disabled={!pending || busy}
              className="inline-flex items-center gap-1.5 rounded bg-accent hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2"
            >
              <Upload className="w-4 h-4" aria-hidden="true" />
              {busy ? 'Working…' : 'Save photo'}
            </button>
            {pending && (
              <button
                type="button"
                onClick={clearPending}
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded border border-border-base text-fg-secondary hover:text-fg-primary text-sm px-3 py-2"
              >
                <X className="w-4 h-4" aria-hidden="true" />
                Cancel
              </button>
            )}
            {record.headshot_path && !pending && (
              <button
                type="button"
                onClick={handleRemove}
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded border border-status-danger-bg text-status-danger hover:bg-status-danger-bg/20 text-sm px-3 py-2"
              >
                <Trash2 className="w-4 h-4" aria-hidden="true" />
                Remove photo
              </button>
            )}
          </div>

          {error && <div className="text-sm text-status-danger">{error}</div>}
          {notice && <div className="text-sm text-status-success">{notice}</div>}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The editable profile
// ---------------------------------------------------------------------------

/** Socials get a local row id so React keys survive reordering and removal. */
interface SocialRow extends CandidateSocialInput {
  rowId: string;
}

let socialRowSeq = 0;
function newRowId(): string {
  socialRowSeq += 1;
  return `social-${socialRowSeq}`;
}

function toRows(record: CandidateRead): SocialRow[] {
  return record.socials.map((s) => ({
    rowId: newRowId(),
    platform: s.platform,
    handle: s.handle,
    url: s.url,
    display_order: s.display_order,
  }));
}

function ProfileForm({
  record,
  isMobile,
  onSaved,
}: {
  record: CandidateRead;
  isMobile: boolean;
  onSaved: (updated: CandidateRead) => void;
}) {
  const [displayName, setDisplayName] = useState(record.display_name ?? '');
  const [bio, setBio] = useState(record.bio ?? '');
  const [websiteUrl, setWebsiteUrl] = useState(record.website_url ?? '');
  const [donateUrl, setDonateUrl] = useState(record.donate_url ?? '');
  const [socials, setSocials] = useState<SocialRow[]>(() => toRows(record));

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // A social row with no URL cannot be saved — the server requires it — so
  // catch it here rather than letting the whole save fail on one blank row.
  const incompleteSocials = useMemo(
    () => socials.some((s) => s.url.trim().length === 0),
    [socials],
  );

  const addSocial = useCallback(() => {
    setSocials((prev) => [
      ...prev,
      { rowId: newRowId(), platform: 'facebook', handle: null, url: '', display_order: prev.length },
    ]);
  }, []);

  const removeSocial = useCallback((rowId: string) => {
    setSocials((prev) => prev.filter((s) => s.rowId !== rowId));
  }, []);

  const patchSocial = useCallback((rowId: string, patch: Partial<CandidateSocialInput>) => {
    setSocials((prev) => prev.map((s) => (s.rowId === rowId ? { ...s, ...patch } : s)));
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (saving || incompleteSocials) return;
      setSaving(true);
      setError(null);
      setSuccess(null);
      try {
        const updated = await updateMyCandidateProfile(record.id, {
          display_name: nullIfBlank(displayName),
          bio: nullIfBlank(bio),
          website_url: nullIfBlank(websiteUrl),
          donate_url: nullIfBlank(donateUrl),
          // Always present, so this is always a replace-all. The form holds
          // every row, so "what is on screen" and "what is stored" is the
          // whole contract — there is no partial-socials state to reconcile.
          socials: socials.map((s, i) => ({
            platform: s.platform,
            handle: s.handle && s.handle.trim() ? s.handle.trim() : null,
            url: s.url.trim(),
            display_order: i,
          })),
        });
        onSaved(updated);
        setSocials(toRows(updated));
        setSuccess('Profile saved.');
      } catch (err) {
        setError(errorDetail(err));
      } finally {
        setSaving(false);
      }
    },
    [saving, incompleteSocials, record.id, displayName, bio, websiteUrl, donateUrl, socials, onSaved],
  );

  const fieldGrid = isMobile
    ? 'grid grid-cols-1 gap-4'
    : 'grid grid-cols-1 sm:grid-cols-2 gap-4';

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-xl border border-border-base bg-bg-raised p-5 space-y-5"
    >
      <div>
        <h3 className="text-base font-semibold text-fg-primary">About you</h3>
        <p className="text-sm text-fg-secondary mt-0.5">
          What voters and your volunteers see when your race comes up.
        </p>
      </div>

      {success && (
        <div className="rounded-lg border border-status-success-bg bg-status-success-bg/30 p-3 text-sm text-status-success">
          {success}
        </div>
      )}
      {error && (
        <div className="rounded-lg border border-status-danger-bg bg-status-danger-bg/30 p-3 text-sm text-status-danger">
          {error}
        </div>
      )}

      <div className={fieldGrid}>
        <Field
          label="Display name"
          hint="Short form used in canvasser scripts — what a volunteer actually says at the door. Leave blank to use your ballot name."
        >
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={record.full_name}
            maxLength={60}
            className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary placeholder-fg-muted text-sm"
          />
        </Field>

        <Field label="Campaign website" hint="Optional.">
          <input
            type="url"
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            placeholder="https://example.com"
            maxLength={500}
            className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary placeholder-fg-muted text-sm"
          />
        </Field>

        <Field label="Donate link" hint="Optional.">
          <input
            type="url"
            value={donateUrl}
            onChange={(e) => setDonateUrl(e.target.value)}
            placeholder="https://example.com/donate"
            maxLength={500}
            className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary placeholder-fg-muted text-sm"
          />
        </Field>
      </div>

      <Field label="Bio" hint="A few sentences. Plain text.">
        <textarea
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          rows={7}
          placeholder="Who you are and why you're running."
          className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary placeholder-fg-muted text-sm leading-relaxed"
        />
      </Field>

      {/* Socials */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-fg-primary">Social accounts</div>
            <p className="text-xs text-fg-muted mt-0.5">
              A link is enough — the handle is optional.
            </p>
          </div>
          <button
            type="button"
            onClick={addSocial}
            className="inline-flex items-center gap-1.5 rounded border border-border-base text-fg-secondary hover:text-fg-primary text-xs font-semibold px-3 py-1.5"
          >
            <Plus className="w-3.5 h-3.5" aria-hidden="true" />
            Add
          </button>
        </div>

        {socials.length === 0 ? (
          <div className="rounded-lg border border-border-base bg-bg-base p-3 text-sm text-fg-secondary">
            No social accounts linked.
          </div>
        ) : (
          <div className="space-y-2">
            {socials.map((s) => (
              <div
                key={s.rowId}
                className={
                  isMobile
                    ? 'rounded-lg border border-border-base bg-bg-base p-3 space-y-2'
                    : 'rounded-lg border border-border-base bg-bg-base p-3 flex items-start gap-2'
                }
              >
                <select
                  value={s.platform}
                  onChange={(e) =>
                    patchSocial(s.rowId, {
                      platform: e.target.value as CandidateSocialPlatform,
                    })
                  }
                  className={
                    isMobile
                      ? 'w-full bg-bg-raised border border-border-base rounded px-2 py-2 text-fg-primary text-sm'
                      : 'w-36 shrink-0 bg-bg-raised border border-border-base rounded px-2 py-2 text-fg-primary text-sm'
                  }
                  aria-label="Platform"
                >
                  {CANDIDATE_SOCIAL_PLATFORMS.map((p) => (
                    <option key={p} value={p}>
                      {CANDIDATE_SOCIAL_PLATFORM_LABELS[p]}
                    </option>
                  ))}
                </select>

                <input
                  type="url"
                  value={s.url}
                  onChange={(e) => patchSocial(s.rowId, { url: e.target.value })}
                  placeholder="https://facebook.com/…"
                  maxLength={500}
                  className="flex-1 w-full bg-bg-raised border border-border-base rounded px-3 py-2 text-fg-primary placeholder-fg-muted text-sm"
                  aria-label="Profile URL"
                />

                <input
                  type="text"
                  value={s.handle ?? ''}
                  onChange={(e) => patchSocial(s.rowId, { handle: e.target.value })}
                  placeholder="@handle"
                  maxLength={100}
                  className={
                    isMobile
                      ? 'w-full bg-bg-raised border border-border-base rounded px-3 py-2 text-fg-primary placeholder-fg-muted text-sm'
                      : 'w-40 shrink-0 bg-bg-raised border border-border-base rounded px-3 py-2 text-fg-primary placeholder-fg-muted text-sm'
                  }
                  aria-label="Handle"
                />

                <button
                  type="button"
                  onClick={() => removeSocial(s.rowId)}
                  className="shrink-0 rounded border border-border-base p-2 text-fg-muted hover:text-status-danger"
                  aria-label="Remove this account"
                >
                  <X className="w-4 h-4" aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
        )}

        {incompleteSocials && (
          <p className="text-[11px] text-status-warning">
            Every social account needs a link. Fill it in or remove the row.
          </p>
        )}
      </div>

      <div className="flex items-center gap-3 pt-1">
        <button
          type="submit"
          disabled={saving || incompleteSocials}
          className="inline-flex items-center gap-1.5 rounded bg-accent hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2"
        >
          <Save className="w-4 h-4" aria-hidden="true" />
          {saving ? 'Saving…' : 'Save profile'}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// The read-only half
// ---------------------------------------------------------------------------

function BallotFactsCard({ record }: { record: CandidateRead }) {
  const facts: Array<[string, string]> = [
    ['Ballot name', record.full_name],
    ['Office', record.office],
    ['Party', record.party ?? '—'],
    ['District', record.district_label ?? 'County-wide'],
    ['Election date', record.election_date ?? '—'],
  ];

  return (
    <div className="rounded-xl border border-border-base bg-bg-raised p-5">
      <div className="text-[10px] uppercase tracking-wider text-fg-muted font-semibold mb-1">
        Set by your committee
      </div>
      <p className="text-sm text-fg-secondary mb-4">
        These decide which doors and which responses belong to your race, so
        they aren&apos;t editable here. Ask your committee admin if one is wrong.
      </p>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
        {facts.map(([label, value]) => (
          <div key={label} className="flex flex-col">
            <dt className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold">
              {label}
            </dt>
            <dd className="text-sm text-fg-primary">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold">
        {label}
        {required && <span className="text-status-danger ml-0.5">*</span>}
      </span>
      {children}
      {hint && <span className="text-[11px] text-fg-muted">{hint}</span>}
    </label>
  );
}

export default MyProfileView;
